/**
 * Conversion between the DAQUMA "netlist" (what the backend stores in
 * `pipeline_version.graph_json`) and React Flow's node/edge shape.
 *
 * Netlist node:  { id, type, config, pos:{x,y} }
 * Netlist edge:  { from, to }
 * React Flow:    { id, type:"pipelineNode", position, data:{ kind, config } }
 *               { id, source, target }
 */

import { MarkerType, type Edge, type Node } from "@xyflow/react";
import { NODE_BY_KIND, isNoteKind } from "./nodeRegistry";

export interface DaqNode {
  id: string;
  type: string;
  config: Record<string, unknown>;
  pos: { x: number; y: number };
  disabled?: boolean;
}

export interface DaqEdge {
  from: string;
  to: string;
  /** Source output handle id (default "out") and target input handle id
   *  (default "in-0"). Multiple input ports let a node — e.g. Display — accept
   *  several inputs, each on its own handle. */
  fromPort?: string;
  toPort?: string;
}

/** One value a run supplies, declared on the pipeline rather than on a node.
 *
 *  A migration is run per wave, per delta, per market — and without parameters
 *  each of those is a copy of the pipeline that drifts from the others. */
export interface DaqParameter {
  name: string;
  label?: string;
  description?: string;
  default?: string;
  required?: boolean;
}

export interface PipelineGraph extends Record<string, unknown> {
  entry: string | null;
  nodes: DaqNode[];
  edges: DaqEdge[];
  /** Absent on every pipeline that declares none, which is most of them. */
  parameters?: DaqParameter[];
}

export function toReactFlow(graph: PipelineGraph): { nodes: Node[]; edges: Edge[] } {
  const nodes: Node[] = (graph.nodes ?? []).map((n) => {
    const base: Node = {
      id: n.id,
      type: "pipelineNode",
      position: { x: n.pos?.x ?? 0, y: n.pos?.y ?? 0 },
      data: { kind: n.type, config: n.config ?? {}, disabled: n.disabled ?? false },
    };
    return isNoteKind(n.type) ? asNoteNode(base, n.config ?? {}) : base;
  });
  const edges: Edge[] = (graph.edges ?? []).map((e) => ({
    id: `${e.from}:${e.fromPort ?? "out"}->${e.to}:${e.toPort ?? "in-0"}`,
    source: e.from,
    target: e.to,
    sourceHandle: e.fromPort ?? "out",
    targetHandle: e.toPort ?? "in-0",
  }));
  return { nodes, edges };
}

/** A note renders as its own React Flow type, sized from its config — the size
 *  is saved in `config` so a resized note comes back the size it was left. */
export function asNoteNode(node: Node, config: Record<string, unknown>): Node {
  return {
    ...node,
    type: "noteNode",
    width: Number(config.width) || 220,
    height: Number(config.height) || 120,
  };
}

/** Edge colour and arrowhead. A token rather than a hex, so the canvas reads the
 *  same in both themes. */
const EDGE_COLOR = "var(--mantine-color-gray-5)";

/**
 * Give every edge an arrowhead.
 *
 * Applied where the edges are *rendered* rather than where they are saved: the
 * marker is how the canvas looks, not part of the graph, and writing it into
 * `graph_json` would put presentation in an immutable version snapshot.
 *
 * Not `defaultEdgeOptions` either — React Flow applies that only to edges made
 * by dragging a connection, so a pipeline loaded from a saved version would
 * come back without arrows and gain them one at a time as you edited it.
 */
export function withArrows(edges: Edge[]): Edge[] {
  return edges.map((edge) => ({
    ...edge,
    markerEnd: { type: MarkerType.ArrowClosed, width: 18, height: 18, color: EDGE_COLOR },
    style: { ...edge.style, stroke: EDGE_COLOR, strokeWidth: 1.5 },
  }));
}

export function fromReactFlow(
  nodes: Node[],
  edges: Edge[],
  parameters: DaqParameter[] = [],
): PipelineGraph {
  const daqNodes: DaqNode[] = nodes.map((n) => {
    const type = String(n.data?.kind ?? "");
    let config = (n.data?.config ?? {}) as Record<string, unknown>;
    // A note's size lives on the React Flow node while it is being resized;
    // fold it back into config so it is saved with the graph.
    if (isNoteKind(type) && n.width && n.height) {
      config = { ...config, width: Math.round(n.width), height: Math.round(n.height) };
    }
    return {
      id: n.id,
      type,
      config,
      pos: { x: n.position.x, y: n.position.y },
      disabled: Boolean(n.data?.disabled),
    };
  });
  const daqEdges: DaqEdge[] = edges.map((e) => ({
    from: e.source,
    to: e.target,
    fromPort: e.sourceHandle ?? "out",
    toPort: e.targetHandle ?? "in-0",
  }));
  // The entry is the single trigger node (one-graph rule); null if none yet.
  const entry = daqNodes.find((n) => NODE_BY_KIND.get(n.type)?.category === "trigger")?.id ?? null;
  // Omitted rather than written as `[]` when there are none, so a pipeline that
  // never used parameters keeps the graph — and the version hash — it had.
  return parameters.length
    ? { entry, nodes: daqNodes, edges: daqEdges, parameters }
    : { entry, nodes: daqNodes, edges: daqEdges };
}
